import {
  MBA_INSTALLMENT_COUNT,
  MBA_INSTALLMENT_PRICE_CENTS,
  MBA_ONE_TIME_PRICE_CENTS,
} from '../../constants/mba-pricing';
import { ROUTES } from '../../constants/routes';
import { isSafeDestinationPath } from '../http/safe-destination-path';

/** Why a visitor is signing up: browsing for free, or on the way to paying for the MBA. */
export const SIGNUP_INTENTS = ['free', 'premium'] as const;
export type SignupIntent = (typeof SIGNUP_INTENTS)[number];

/** The MBA plan a paid button chose. The values match the pricing page's `plan` query values. */
export const SIGNUP_PLANS = ['installment', 'one_time'] as const;
export type SignupPlan = (typeof SIGNUP_PLANS)[number];

export interface SignupIntentSelection {
  intent: SignupIntent;
  plan?: SignupPlan;
}

/** Base path for the server copies of browser-originated Meta funnel events. */
export const META_FUNNEL_EVENTS_API_PATH = '/api/meta';
/** Server copy of the paid-plan click's Meta InitiateCheckout. */
export const META_INITIATE_CHECKOUT_API_PATH = `${META_FUNNEL_EVENTS_API_PATH}/initiate-checkout`;
/** Server copy of the custom OnboardingComplete funnel event. */
export const META_ONBOARDING_COMPLETE_API_PATH = `${META_FUNNEL_EVENTS_API_PATH}/onboarding-complete`;

/** The anchor every Enroll button scrolls to on pages that render the MBA pricing section. */
export const MBA_PRICING_SECTION_ID = 'pricing';

export function parseSignupIntent(value: string | null | undefined): SignupIntent | undefined {
  return SIGNUP_INTENTS.find(intent => intent === value);
}

export function parseSignupPlan(value: string | null | undefined): SignupPlan | undefined {
  return SIGNUP_PLANS.find(plan => plan === value);
}

/** Reads `intent` and `plan` from a query string. A plan only counts with a premium intent. */
export function readSignupIntentParams(params: URLSearchParams): SignupIntentSelection | undefined {
  const intent = parseSignupIntent(params.get('intent'));
  if (!intent) return undefined;
  const plan = intent === 'premium' ? parseSignupPlan(params.get('plan')) : undefined;
  return plan ? { intent, plan } : { intent };
}

/**
 * Resolves the visitor's intent: the button that was clicked (carried in the URL it opened) wins, then whatever an
 * earlier page saved, then free. A plain URL never makes anyone a buyer.
 */
export function resolveSignupIntent(
  fromUrl: SignupIntentSelection | undefined,
  saved: SignupIntentSelection | undefined,
): SignupIntentSelection {
  return fromUrl ?? saved ?? { intent: 'free' };
}

/**
 * `/signup?intent=…&plan=…`, the destination of every landing signup button. A safe `redirect` keeps the page the
 * button promised; signup persists it so onboarding ends there.
 */
export function buildSignupIntentHref(selection: SignupIntentSelection, redirect?: string): string {
  const params = new URLSearchParams({ intent: selection.intent });
  if (selection.intent === 'premium' && selection.plan) params.set('plan', selection.plan);
  if (isSafeDestinationPath(redirect)) params.set('redirect', redirect);
  return `${ROUTES.SIGNUP}?${params.toString()}`;
}

/** The payment step of pricing with the plan preselected. */
export function buildMbaCheckoutHref(plan: SignupPlan): string {
  const params = new URLSearchParams({ plan, step: 'payment' });
  return `${ROUTES.PRICING}?${params.toString()}`;
}

/**
 * The InitiateCheckout value for a plan. A checkout that knows its quoted (discounted) plan total reports that; it is
 * accepted only when positive, finite and at most the list price, otherwise the list price stands. The browser and
 * the server both call this so the two copies of one event carry the same value.
 */
export function resolveMbaInitiateCheckoutValueUsd(plan: SignupPlan, quotedValue?: number | null): number {
  const listValue = getMbaPlanValueUsd(plan);
  if (typeof quotedValue !== 'number' || !Number.isFinite(quotedValue)) return listValue;
  const rounded = Math.round(quotedValue * 100) / 100;
  return rounded > 0 && rounded <= listValue ? rounded : listValue;
}

/** Whether a member-app destination is checkout, which buyers reach before onboarding. */
export function isCheckoutDestination(path: string | null | undefined): boolean {
  return path === ROUTES.PRICING || path?.startsWith(`${ROUTES.PRICING}?`) === true;
}

/**
 * A premium intent opens checkout: the chosen plan's payment step, or the plan picker when no plan was chosen.
 * A free intent follows the normal path.
 */
export function resolveSignupIntentDestination(selection: SignupIntentSelection): string | undefined {
  if (selection.intent !== 'premium') return undefined;
  return selection.plan ? buildMbaCheckoutHref(selection.plan) : ROUTES.PRICING;
}

/** The whole programme price each plan commits to, reported as the InitiateCheckout value. */
export function getMbaPlanValueUsd(plan: SignupPlan): number {
  const cents = plan === 'one_time' ? MBA_ONE_TIME_PRICE_CENTS : MBA_INSTALLMENT_PRICE_CENTS * MBA_INSTALLMENT_COUNT;
  return cents / 100;
}
